import { NaturalEntityDataType } from "@/features/utils/types";
import { useUser } from "./use-user";
import { UserType } from "../types";
import { companyDetails } from "@/features/company/types";
import { useCompany } from "@/features/company/hooks/use-company";
import { useCompanyContext } from "@/features/company/hooks/use-company-context";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

export const useSetAliasPublic = () => {
  const { useEditProfile } = useUser();
  const { editCompanyDataMutation } = useCompany();
  const { leUuid } = useCompanyContext();
  const { mutateAsync: editProfile } = useEditProfile();
  const { mutateAsync: editCompany } = editCompanyDataMutation();
  const { t } = useTranslation(["pass"]);

  const setAliasPublic = async (
    type:
      | NaturalEntityDataType.AliasIBANDto
      | NaturalEntityDataType.AliasMobileDto
      | NaturalEntityDataType.AliasMailDto,
    uuid: string,
    isPublic: boolean,
    profile?: UserType,
    companyDetails?: companyDetails | null
  ) => {
    if (!leUuid) {
      if (!profile) return;
      if (type === NaturalEntityDataType.AliasIBANDto) {
        const iban = profile?.naturalPerson.AliasIBAN.find((iban) => iban.UUID === uuid);
        if (!iban) {
          return;
        }
        if (iban && iban.valid === false) {
          return;
        }
        await editProfile({
          typeDto: NaturalEntityDataType.AliasIBANDto,
          UUID: profile?.naturalPerson.UUID || "",
          AliasIBAN: { ...iban, Public: isPublic }
        });
      } else if (type === NaturalEntityDataType.AliasMobileDto) {
        const mobile = profile?.naturalPerson.AliasMobile.find((mobile) => mobile.UUID === uuid);
        if (!mobile) {
          return;
        }
        await editProfile({
          typeDto: NaturalEntityDataType.AliasMobileDto,
          UUID: profile?.naturalPerson.UUID || "",
          AliasMobile: { ...mobile, Public: isPublic }
        });
      } else if (type === NaturalEntityDataType.AliasMailDto) {
        const mail = profile?.naturalPerson.AliasEmail.find((mail) => mail.UUID === uuid);
        if (!mail) {
          return;
        }
        await editProfile({
          typeDto: NaturalEntityDataType.AliasMailDto,
          UUID: profile?.naturalPerson.UUID || "",
          AliasEmail: { ...mail, Public: isPublic }
        });
      }
    } else {
      if (!companyDetails || !companyDetails.legalEntity.UUID) return;
      if (type === NaturalEntityDataType.AliasIBANDto) {
        const targetIndex = companyDetails.legalEntity.AliasIBAN.findIndex(
          (item) => item.UUID === uuid
        );
        if (targetIndex === -1) return;
        if (
          companyDetails.legalEntity.AliasIBAN[targetIndex] &&
          companyDetails.legalEntity.AliasIBAN[targetIndex].valid === false
        ) {
          return;
        }
        companyDetails.legalEntity.AliasIBAN[targetIndex].Public = isPublic;
        try {
          await editCompany({ ...companyDetails, uuid: companyDetails.legalEntity.UUID });
        } catch (_error) {
          toast.error(t("olkypass.companyPage.notifications.error.update_company"));
          companyDetails.legalEntity.AliasIBAN[targetIndex].Public = !isPublic;
        }
      } else if (type === NaturalEntityDataType.AliasMobileDto) {
        const targetIndex = companyDetails.legalEntity.AliasMobile.findIndex(
          (item) => item.UUID === uuid
        );
        if (targetIndex === -1) return;
        companyDetails.legalEntity.AliasMobile[targetIndex].Public = isPublic;
        try {
          await editCompany({ ...companyDetails, uuid: companyDetails.legalEntity.UUID });
        } catch (_error) {
          toast.error(t("olkypass.companyPage.notifications.error.update_company"));
          companyDetails.legalEntity.AliasMobile[targetIndex].Public = !isPublic;
        }
      } else if (type === NaturalEntityDataType.AliasMailDto) {
        const targetIndex = companyDetails.legalEntity.AliasEmail.findIndex(
          (item) => item.UUID === uuid
        );
        if (targetIndex === -1) return;
        companyDetails.legalEntity.AliasEmail[targetIndex].Public = isPublic;
        try {
          await editCompany({ ...companyDetails, uuid: companyDetails.legalEntity.UUID });
        } catch (_error) {
          toast.error(t("olkypass.companyPage.notifications.error.update_company"));
          companyDetails.legalEntity.AliasEmail[targetIndex].Public = !isPublic;
        }
      }
    }
  };

  return {
    setAliasPublic
  };
};
